/**
 * AtlasShaderFxModule — the per-frame drivers + diag for shader waves 3 and 4
 * (2026-09-09): interior-mapping windows light up with the night, soft
 * particles track the ground camera's far plane, the cloud-shadow layers
 * drift with the cloud field's wind and darken with its coverage, the rain
 * ripples follow the precipitation; `window.__stage3()` / `__stage4()` report
 * every counter the probes assert.
 *
 * Runs at RenderSub/9 (after the sky uniforms at RenderSub/5 so this frame's
 * night factor is current, beside the street lamps at 8).
 */
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor } from './SkyModule.js';
import { activePreset } from '../lib/quality_tiers.js';
import { STAGE3_FLAGS, interiorHandles, softHandles, litRatioForNight, stage3Stats } from '../lib/stage3_fx.js';
import { STAGE4_FLAGS, cloudShadowHandles, rainHandles, cloudShadowStrength, rainRippleIntensity, stage4Stats } from '../lib/stage4_fx.js';
import { STAGE5_FLAGS, sixWayHandles, retroHandles, specularAAHandles, stage5Stats, sixWayLightFor, headlightFor, specularAAVarianceCap } from '../lib/stage5_fx.js';
import { updateLitSmoke, litSmokeStats } from '../lib/lit_smoke.js';
import { uSunDir } from './SkyModule.js';
import { activeTier } from '../lib/quality_tiers.js';
import * as THREE from 'three/webgpu';

export class AtlasShaderFxModule implements IModule {
  readonly name = 'ShaderFx';
  private app!: App;
  private lastLit = -1;
  private frame = 0;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasShaderFx', Phase.RenderSub, 9, () => this.tick()));
    (globalThis as unknown as { __stage3?: () => unknown }).__stage3 = () => {
      const p = activePreset();
      const flags: Record<string, boolean> = {};
      for (const k of STAGE3_FLAGS) flags[k] = !!(p as unknown as Record<string, unknown>)[k];
      return {
        ...stage3Stats,
        flags,
        interiorHandles: interiorHandles.size,
        interiorLit: this.lastLit,
        softHandles: softHandles.size,
        night: (uNightFactor as unknown as { value: number }).value,
        webgpu: !!this.app.renderer.isWebGPU,
      };
    };
    this.registerStage5();
    (globalThis as unknown as { __stage4?: () => unknown }).__stage4 = () => {
      const p = activePreset();
      const flags: Record<string, boolean> = {};
      for (const k of STAGE4_FLAGS) flags[k] = !!(p as unknown as Record<string, unknown>)[k];
      return {
        ...stage4Stats,
        flags,
        bloomMode: p.post.bloom ? ((p.post.bloom as { mode?: string }).mode ?? 'mip') : 'off',
        cloudShadowHandles: cloudShadowHandles.size,
        rainHandles: rainHandles.size,
        night: (uNightFactor as unknown as { value: number }).value,
        webgpu: !!this.app.renderer.isWebGPU,
      };
    };
  }

  private readonly _sunView = new THREE.Vector3();
  private lastCap = -1;

  private registerStage5(): void {
    (globalThis as unknown as { __stage5?: () => unknown }).__stage5 = () => {
      const p = activePreset();
      const flags: Record<string, boolean> = {};
      for (const k of STAGE5_FLAGS) flags[k] = !!(p as unknown as Record<string, unknown>)[k];
      return {
        ...stage5Stats,
        flags,
        exposureMetering: p.autoExposureMetering ?? 'average',
        sixWayHandles: sixWayHandles.size,
        retroHandles: retroHandles.size,
        specularAAHandles: specularAAHandles.size,
        litSmoke: litSmokeStats(),
        night: (uNightFactor as unknown as { value: number }).value,
        webgpu: !!this.app.renderer.isWebGPU,
      };
    };
  }

  /** Shader additions wave (2026-09-15): the per-frame drivers, uniform writes only. */
  private tickStage5(): void {
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    const sun = uSunDir.value as THREE.Vector3;
    if (sixWayHandles.size && this.frame % 3 === 0) {
      const L = sixWayLightFor([sun.x, sun.y, sun.z], night);
      for (const h of sixWayHandles) {
        h.lightDirWorld.value.set(L.dir[0], L.dir[1], L.dir[2]);
        h.lightColor.value.set(L.color[0], L.color[1], L.color[2]);
        h.ambientColor.value.set(L.ambient[0], L.ambient[1], L.ambient[2]);
      }
    }
    if (retroHandles.size) {
      // The sun in VIEW space by day; at night on a ride, the headlight — a
      // light at the viewer, which is exactly what a retro film answers to.
      const cam = this.app.renderer.cameras.main as THREE.Camera | undefined;
      const head = headlightFor(!!atlas.hoverRide, night, 1);
      if (cam) {
        this._sunView.copy(sun).transformDirection(cam.matrixWorldInverse);
        const day = (1 - night) * Math.max(0, Math.min(1, (sun.y + 0.02) * 3));
        for (const h of retroHandles) {
          if (head > day) { h.lightDirView.value.set(0, 0, 1); h.lightColor.value.set(head, head * 0.95, head * 0.85); }
          else { h.lightDirView.value.set(this._sunView.x, this._sunView.y, this._sunView.z); h.lightColor.value.set(day, day * 0.95, day * 0.88); }
        }
      }
    }
    if (specularAAHandles.size && this.frame % 30 === 0) {
      const cap = specularAAVarianceCap(activeTier());
      if (cap !== this.lastCap) { this.lastCap = cap; for (const h of specularAAHandles) h.varianceCap.value = cap; }
    }
    updateLitSmoke();
  }

  private tick(): void {
    if (atlas.spaceFlight) return;
    this.frame++;
    if (interiorHandles.size) {
      const lit = litRatioForNight((uNightFactor as unknown as { value: number }).value ?? 0);
      if (Math.abs(lit - this.lastLit) > 0.005) {
        this.lastLit = lit;
        for (const h of interiorHandles) h.litRatio.value = lit;
      }
    }
    if (softHandles.size) {
      // The soft fade measures the depth difference in metres = normalised
      // depth × the camera far; the ground camera's far plane is 120 km and
      // the space camera's differs — follow the main camera every frame.
      let far = 0;
      try { far = (this.app.renderer.cameras.main as { far?: number } | undefined)?.far ?? 0; } catch { far = 0; }
      if (far > 0) for (const h of softHandles) if (h.cameraFar.value !== far) h.cameraFar.value = far;
    }
    // Stage 4 drivers (uniform writes only): rain every frame — the weather's
    // precipitation kind can flip between refreshes and a 6-frame cadence is
    // seconds under a slow renderer; cloud shadows at ~10 Hz.
    if (cloudShadowHandles.size || rainHandles.size) {
      const weather = this.app.getModule('Weather') as { getCloudCoverage?: () => number; getPrecip?: () => { kind: string; density: number } } | undefined;
      if (cloudShadowHandles.size && this.frame % 6 === 0) {
        const clouds = this.app.getModule('Clouds') as { renderer?: { windOffset?: { x: number; y: number } } } | undefined;
        const wind = clouds?.renderer?.windOffset ?? null;
        const strength = cloudShadowStrength(weather?.getCloudCoverage?.() ?? 0.4);
        stage4Stats.cloudShadowStrength = strength;
        // The layer samples positionWorld, which the FP64 rebase shifts by the
        // origin (scene.position = −origin); the clouds sample ABSOLUTE XZ —
        // fold the origin into the drift so the shadow stays under its cloud.
        const sp = this.app.world.scene.position;
        const ox = (wind?.x ?? 0) - sp.x, oz = (wind?.y ?? 0) - sp.z;
        for (const h of cloudShadowHandles) { h.strength.value = strength; h.offset.value.set(ox, oz); }
      }
      if (rainHandles.size) {
        const pr = weather?.getPrecip?.() ?? { kind: 'none', density: 0 };
        const v = rainRippleIntensity(pr.kind, pr.density);
        stage4Stats.rainIntensity = v;
        for (const h of rainHandles) h.intensity.value = v;
      }
    }
    this.tickStage5();
  }
}
